import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { RentalService } from '../../../core/api/rental.service';
import { RentalList, RentalStatus } from '../../../core/models/rental.model';
import { PaginatedResponse } from '../../../core/models/api.model';
import { RentalCardComponent } from '../rental-card/rental-card.component';
import { PaginationComponent, PageChange } from '../../../shared/components/pagination/pagination.component';

interface TabState {
  rentals: RentalList[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

// Tab index constants for readability.
const TAB_ALL = 0;
const TAB_ISSUED = 1;
const TAB_RETURNED = 2;

@Component({
  selector: 'app-rental-list',
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule, MatIconModule, MatButtonModule,
    MatTabsModule, MatProgressSpinnerModule, MatFormFieldModule, MatInputModule,
    RentalCardComponent, PaginationComponent,
  ],
  templateUrl: './rental-list.component.html',
  styleUrl: './rental-list.component.scss',
})
export class RentalListComponent implements OnInit, OnDestroy {
  selectedTab = TAB_ALL;
  readonly tabs: TabState[] = [
    this.makeTabState(),
    this.makeTabState(),
    this.makeTabState(),
  ];

  // Shared across all tabs: search query is debounced and then applied
  // when reloading the currently selected tab. Switching tabs preserves it.
  searchQuery: string = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  private fragmentSub?: Subscription;

  constructor(
    private rentalService: RentalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // Initial tab is driven by the current URL fragment (deep-link friendly).
    this.selectedTab = this.fragmentToIndex(this.route.snapshot.fragment);
    this.fragmentSub = this.route.fragment.subscribe((fragment) => {
      const idx = this.fragmentToIndex(fragment);
      // Only react to external fragment changes (e.g. browser back/forward).
      // The selectedIndexChange handler manages user clicks; this guard
      // prevents a feedback loop between the two paths.
      if (idx !== this.selectedTab) {
        this.selectedTab = idx;
        this.loadTab(idx);
      }
    });
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.searchQuery = value;
        this.resetPageAndReload();
      });
    this.loadTab(this.selectedTab);
  }

  ngOnDestroy(): void {
    this.fragmentSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchSubject.next('');
    this.resetPageAndReload();
  }

  onTabChange(index: number): void {
    if (index === this.selectedTab) {
      return;
    }
    this.selectedTab = index;
    const targetFragment = this.indexToFragment(index);
    const currentFragment = this.route.snapshot.fragment || null;
    // Only push a new history entry when the fragment actually changes;
    // this keeps the back/forward stack clean.
    if (targetFragment !== currentFragment) {
      this.router.navigate([], {
        relativeTo: this.route,
        fragment: targetFragment ?? undefined,
        replaceUrl: false,
      });
    }
    this.loadTab(index);
  }

  onPageChange(change: PageChange, tabIndex: number): void {
    const state = this.tabs[tabIndex];
    if (change.pageSize !== state.pageSize) {
      state.pageSize = change.pageSize;
    }
    state.page = change.page;
    this.loadTab(tabIndex);
  }

  getShowHandoverLabel(tabIndex: number): boolean {
    // Match the original behaviour: handover label only on the ISSUED tab.
    return tabIndex === TAB_ISSUED;
  }

  private makeTabState(): TabState {
    return {
      rentals: [],
      loading: false,
      page: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 1,
    };
  }

  private fragmentToIndex(fragment: string | null): number {
    switch (fragment) {
      case 'issued':
        return TAB_ISSUED;
      case 'returned':
        return TAB_RETURNED;
      default:
        return TAB_ALL;
    }
  }

  private indexToFragment(index: number): string | null {
    switch (index) {
      case TAB_ISSUED:
        return 'issued';
      case TAB_RETURNED:
        return 'returned';
      default:
        return null;
    }
  }

  private indexToStatus(index: number): string | null {
    switch (index) {
      case TAB_ISSUED:
        return RentalStatus.ISSUED;
      case TAB_RETURNED:
        return RentalStatus.RETURNED;
      default:
        return null;
    }
  }

  private loadTab(index: number): void {
    const state = this.tabs[index];
    state.loading = true;
    let params = new HttpParams()
      .set('page', String(state.page))
      .set('limit', String(state.pageSize));
    const status = this.indexToStatus(index);
    if (status) {
      params = params.set('status', status);
    }
    if (this.searchQuery) {
      params = params.set('search', this.searchQuery);
    }
    this.rentalService.list(params).subscribe({
      next: (res: PaginatedResponse<RentalList>) => {
        state.rentals = res.results;
        state.page = res.page;
        state.totalPages = res.pages;
        state.totalItems = res.count;
        state.loading = false;
      },
      error: () => { state.loading = false; },
    });
  }

  private resetPageAndReload(): void {
    const state = this.tabs[this.selectedTab];
    state.page = 1;
    this.loadTab(this.selectedTab);
  }
}
